import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { X, Clock, Globe, Calendar, Save, AlertTriangle, MapPin } from 'lucide-react';
import ZoneSelector from './ZoneSelector';


interface IntrusionDetectionSetupProps {
  camera: {
    id: string;
    name: string;
  };
  onClose: () => void;
  onSave: (config: IntrusionConfig) => Promise<void>;
  detectionType?: 'intrusion-detection' | 'zone-intrusion-detection';
}

interface IntrusionConfig {
  timezone: string;
  office_start_hour: number;
  office_end_hour: number;
  weekend_detection: boolean;
  holiday_detection: boolean;
  custom_holidays: string[];
}

const COMMON_TIMEZONES = [
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
  { value: 'America/New_York', label: 'Eastern Time (US & Canada)' },
  { value: 'America/Chicago', label: 'Central Time (US & Canada)' },
  { value: 'America/Denver', label: 'Mountain Time (US & Canada)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (US & Canada)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
  { value: 'Asia/Kolkata', label: 'India (IST)' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
  { value: 'Australia/Melbourne', label: 'Melbourne (AEST/AEDT)' },
  { value: 'Australia/Brisbane', label: 'Brisbane (AEST)' },
  { value: 'Australia/Perth', label: 'Perth (AWST)' },
  { value: 'Australia/Adelaide', label: 'Adelaide (ACST/ACDT)' },
  { value: 'Australia/Darwin', label: 'Darwin (ACST)' },
  { value: 'Australia/Hobart', label: 'Hobart (AEST/AEDT)' },
  { value: 'Australia/Canberra', label: 'Canberra (AEST/AEDT)' }
];

export function IntrusionDetectionSetup({ camera, onClose, onSave, detectionType = 'intrusion-detection' }: IntrusionDetectionSetupProps) {
  const [config, setConfig] = useState<IntrusionConfig>({
    timezone: 'UTC',
    office_start_hour: 8,
    office_end_hour: 18,
    weekend_detection: true,
    holiday_detection: true,
    custom_holidays: []
  });
  const [loading, setLoading] = useState(false);
  const [newHoliday, setNewHoliday] = useState('');
  const [showZoneSelector, setShowZoneSelector] = useState(false);
  const [hasZone, setHasZone] = useState(false);
  const [currentStep, setCurrentStep] = useState<'config' | 'zone'>('config');

  useEffect(() => {
    loadCurrentConfig();
    checkExistingZone();
  }, [camera.id]);

  const loadCurrentConfig = async () => {
    try {
      const token = localStorage.getItem('csense_auth_token');
      const endpoint = detectionType === 'zone-intrusion-detection' 
        ? `/api/detection/zone-intrusion/config/${camera.id}`
        : `/api/detection/intrusion/config/${camera.id}`;
      const response = await fetch(endpoint, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setConfig({
          timezone: data.timezone || 'UTC',
          office_start_hour: data.office_start_hour || 8,
          office_end_hour: data.office_end_hour || 18,
          weekend_detection: data.weekend_detection ?? true,
          holiday_detection: data.holiday_detection ?? true,
          custom_holidays: data.custom_holidays || []
        });
      }
    } catch (error) {
      console.error('Failed to load intrusion config:', error);
    }
  };

  const checkExistingZone = async () => {
    // Only check for zones if this is zone-based intrusion detection
    if (detectionType === 'zone-intrusion-detection') {
      try {
        const token = localStorage.getItem('csense_auth_token');
        const response = await fetch(`/api/detection/zone-intrusion/get-zone/${camera.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }).then(res => res.json());
        setHasZone(response.polygon_points && response.polygon_points.length > 0);
      } catch (error) {
        console.log('No existing intrusion zone found');
        setHasZone(false);
      }
    } else {
      setHasZone(false); // General intrusion detection doesn't use zones
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await onSave(config);
      onClose();
    } catch (error) {
      console.error('Failed to save intrusion config:', error);
    } finally {
      setLoading(false);
    }
  };

  const addHoliday = () => {
    if (newHoliday && !config.custom_holidays.includes(newHoliday)) {
      setConfig(prev => ({
        ...prev,
        custom_holidays: [...prev.custom_holidays, newHoliday]
      }));
      setNewHoliday('');
    }
  };

  const removeHoliday = (holiday: string) => {
    setConfig(prev => ({
      ...prev,
      custom_holidays: prev.custom_holidays.filter(h => h !== holiday)
    }));
  };

  const formatTime = (hour: number) => {
    return `${hour.toString().padStart(2, '0')}:00`;
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="bg-gray-900 border-gray-800 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-white flex items-center gap-2">
                <Clock className="h-5 w-5" />
                {detectionType === 'zone-intrusion-detection' ? 'Zone-Based Intrusion Detection' : 'General Intrusion Detection'} Configuration
              </CardTitle>
              <p className="text-gray-400 text-sm mt-1">
                Camera: {camera.name}
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Timezone Selection */}
          <div className="space-y-2">
            <Label className="text-white flex items-center gap-2">
              <Globe className="h-4 w-4" />
              Timezone
            </Label>
            <Select value={config.timezone} onValueChange={(value) => setConfig(prev => ({ ...prev, timezone: value }))}>
              <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                <SelectValue placeholder="Select timezone" />
              </SelectTrigger>
              <SelectContent className="bg-gray-800 border-gray-700">
                {COMMON_TIMEZONES.map((tz) => (
                  <SelectItem key={tz.value} value={tz.value} className="text-white hover:bg-gray-700">
                    {tz.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-400">
              Detection will use this timezone for office hours calculation
            </p>
          </div>

          {/* Office Hours */}
          <div className="space-y-4">
            <Label className="text-white flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Office Hours
            </Label>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300 text-sm">Start Time</Label>
                <Select 
                  value={config.office_start_hour.toString()} 
                  onValueChange={(value) => setConfig(prev => ({ ...prev, office_start_hour: parseInt(value) }))}
                >
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700">
                    {Array.from({ length: 24 }, (_, i) => (
                      <SelectItem key={i} value={i.toString()} className="text-white hover:bg-gray-700">
                        {formatTime(i)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-gray-300 text-sm">End Time</Label>
                <Select 
                  value={config.office_end_hour.toString()} 
                  onValueChange={(value) => setConfig(prev => ({ ...prev, office_end_hour: parseInt(value) }))}
                >
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700">
                    {Array.from({ length: 24 }, (_, i) => (
                      <SelectItem key={i} value={i.toString()} className="text-white hover:bg-gray-700">
                        {formatTime(i)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="p-3 bg-blue-900/20 rounded-lg border border-blue-500/30">
              <p className="text-blue-300 text-sm">
                <AlertTriangle className="h-4 w-4 inline mr-1" />
                Intrusion detection will be active outside these hours: 
                <span className="font-mono ml-1">
                  {formatTime(config.office_end_hour)} - {formatTime(config.office_start_hour)}
                </span>
              </p>
            </div>
          </div>

          {/* Weekend & Holiday Detection */}
          <div className="space-y-4">
            <Label className="text-white flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Special Days Detection
            </Label>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                <div>
                  <p className="text-white text-sm font-medium">Weekend Detection</p>
                  <p className="text-gray-400 text-xs">Detect intrusions on Saturdays and Sundays</p>
                </div>
                <Switch
                  checked={config.weekend_detection}
                  onCheckedChange={(checked) => setConfig(prev => ({ ...prev, weekend_detection: checked }))}
                />
              </div>
              
              <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                <div>
                  <p className="text-white text-sm font-medium">Holiday Detection</p>
                  <p className="text-gray-400 text-xs">Detect intrusions on custom holidays</p>
                </div>
                <Switch
                  checked={config.holiday_detection}
                  onCheckedChange={(checked) => setConfig(prev => ({ ...prev, holiday_detection: checked }))}
                />
              </div>
            </div>
          </div>

          {/* Custom Holidays */}
          {config.holiday_detection && (
            <div className="space-y-4">
              <Label className="text-white">Custom Holidays</Label>
              
              <div className="flex gap-2">
                <Input
                  type="date"
                  value={newHoliday}
                  onChange={(e) => setNewHoliday(e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white"
                  placeholder="Select holiday date"
                />
                <Button onClick={addHoliday} size="sm" disabled={!newHoliday}>
                  Add
                </Button>
              </div>
              
              {config.custom_holidays.length > 0 && (
                <div className="space-y-2">
                  <p className="text-gray-400 text-sm">Added Holidays:</p>
                  <div className="flex flex-wrap gap-2">
                    {config.custom_holidays.map((holiday) => (
                      <div key={holiday} className="flex items-center gap-1 bg-gray-800 px-2 py-1 rounded text-sm">
                        <span className="text-white">{holiday}</span>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => removeHoliday(holiday)}
                          className="h-4 w-4 p-0 text-gray-400 hover:text-red-400"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Preview */}
          <div className="p-4 bg-gray-800 rounded-lg border border-gray-700">
            <h4 className="text-white font-medium mb-2">Configuration Preview</h4>
            <div className="text-sm text-gray-300 space-y-1">
              <p><span className="text-gray-400">Timezone:</span> {COMMON_TIMEZONES.find(tz => tz.value === config.timezone)?.label || config.timezone}</p>
              <p><span className="text-gray-400">Office Hours:</span> {formatTime(config.office_start_hour)} - {formatTime(config.office_end_hour)}</p>
              <p><span className="text-gray-400">Detection Active:</span> Outside office hours</p>
              <p><span className="text-gray-400">Weekend Detection:</span> {config.weekend_detection ? 'Enabled' : 'Disabled'}</p>
              <p><span className="text-gray-400">Holiday Detection:</span> {config.holiday_detection ? 'Enabled' : 'Disabled'}</p>
              {config.custom_holidays.length > 0 && (
                <p><span className="text-gray-400">Custom Holidays:</span> {config.custom_holidays.length} configured</p>
              )}
            </div>
          </div>

          {/* Zone Setup Section - Only for zone-based detection */}
          {detectionType === 'zone-intrusion-detection' && (
            <div className="space-y-4">
              <Label className="text-white flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Intrusion Detection Zones
              </Label>
              
              <div className="p-4 bg-gray-800 rounded-lg border border-gray-700">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-white text-sm font-medium">Protected Areas</p>
                    <p className="text-gray-400 text-xs">
                      {hasZone ? 'Zones configured - people entering these areas will trigger alerts' : 'No zones configured - set up zones around areas you want to protect'}
                    </p>
                  </div>
                  <div className={`w-3 h-3 rounded-full ${hasZone ? 'bg-green-500' : 'bg-red-500'}`} />
                </div>
                
                <Button
                  onClick={() => setShowZoneSelector(true)}
                  variant="outline"
                  size="sm"
                  className="border-orange-500 text-orange-400 hover:bg-orange-500/10"
                >
                  <MapPin className="h-4 w-4 mr-2" />
                  {hasZone ? 'Modify Zones' : 'Setup Zones'}
                </Button>
              </div>
              
              <div className="p-3 bg-orange-900/20 rounded-lg border border-orange-500/30">
                <p className="text-orange-300 text-sm">
                  <AlertTriangle className="h-4 w-4 inline mr-1" />
                  <strong>How it works:</strong> Draw zones around areas you want to protect (entrances, storage, equipment). 
                  When someone enters these zones during after-hours, an intrusion alert is triggered.
                </p>
              </div>
            </div>
          )}
          
          {/* General Detection Info - Only for general detection */}
          {detectionType === 'intrusion-detection' && (
            <div className="p-3 bg-blue-900/20 rounded-lg border border-blue-500/30">
              <p className="text-blue-300 text-sm">
                <AlertTriangle className="h-4 w-4 inline mr-1" />
                <strong>General Intrusion Detection:</strong> Any person detected in the camera view during after-hours will trigger an alert. 
                No zones required - the entire camera view is monitored.
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              onClick={handleSave}
              disabled={loading}
              className="bg-green-600 hover:bg-green-700 flex-1"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Configuration
            </Button>
            <Button
              variant="outline"
              onClick={onClose}
              className="border-gray-700 text-gray-300"
            >
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Zone Selector Modal - Only for zone-based detection */}
      {showZoneSelector && detectionType === 'zone-intrusion-detection' && (
        <ZoneSelector
          zoneType="intrusion"
          cameraId={camera.id}
          onSave={(zones: any) => {
            setHasZone(true);
            setShowZoneSelector(false);
          }}
          title="Intrusion Detection Zones"
          description="Draw zones around areas you want to protect. People entering these zones during after-hours will trigger alerts."
        />
      )}
    </div>
  );
}